// «POR REVISAR» · lo que la terminal capturó sin red o no cuadró · DISEÑO-F5 §7.1
//
// Lista de `GET /terminales/{id}/eventos?estado=POR_REVISAR` (F3). Por
// fila, «Aplicar» y «Descartar» (con nota obligatoria, validada en vivo)
// sobre `POST /terminales/eventos/{id}/resolver`. Quien no tiene
// `cuarentena.resolver` ve los botones apagados CON MOTIVO (fachada).
import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { Cargando } from '../ui/EstadoDePantalla';
import { fechaYHora } from '../ui/sesiones';
import { leerErrorDelContrato } from './errorDelContrato';

const POR_PAGINA = 20;
export const MOTIVO_SIN_PERMISO = 'Tu puesto no resuelve movimientos en revisión; pídeselo a un encargado.';

const TIPO = { CONSUMO: 'Sacó', ENTRADA: 'Metió', CONTEO: 'Contó', TRASPASO: 'Pasó a otro lugar', DESHACER: 'Deshizo' };

export function Fila({ ev, puedeResolver, onResuelto, onError }) {
  const [descartando, setDescartando] = useState(false);
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const notaLista = nota.trim().length >= 3;
  const idMotivo = `motivo-revisar-${ev.id}`;

  async function resolver(resolucion) {
    setEnviando(true);
    try {
      const d = await api.post(`/terminales/eventos/${ev.id}/resolver`,
        { resolucion, nota: resolucion === 'DESCARTADO' ? nota.trim() : undefined });
      onResuelto?.(ev, d?.mensaje || (resolucion === 'APLICADO' ? 'Aplicado al inventario.' : 'Descartado.'));
    } catch (e) {
      const { mensaje, queHacer } = leerErrorDelContrato(e);
      onError?.(`${mensaje} ${queHacer}`.trim());
    } finally {
      setEnviando(false);
    }
  }

  return (
    <li className="rounded-xl border border-gray-200 bg-white p-3 space-y-2" data-terminal="evento">
      <div className="font-semibold text-gray-900">
        {TIPO[ev.tipo] || ev.tipo} {ev.cantidad} de {ev.producto || ev.sku}
      </div>
      <div className="text-sm text-gray-600">
        {ev.operador ? `${ev.operador} · ` : ''}{fechaYHora(ev.ocurrio_en)}
        {ev.sku ? ` · ${ev.sku}` : ''}
      </div>
      <div className="text-sm text-amber-800">{ev.motivo_legible || 'Sin motivo registrado: compara la cantidad con la repisa antes de aplicar.'}</div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!puedeResolver || enviando}
          aria-describedby={!puedeResolver ? idMotivo : undefined}
          onClick={() => resolver('APLICADO')}
          className="inline-flex min-h-11 items-center rounded-lg bg-green-700 px-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          Aplicar
        </button>
        <button
          type="button"
          disabled={!puedeResolver || enviando}
          aria-describedby={!puedeResolver ? idMotivo : undefined}
          onClick={() => setDescartando((v) => !v)}
          className="inline-flex min-h-11 items-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 disabled:opacity-50"
        >
          Descartar
        </button>
        <Ayuda id="terminal.revisar.descartar" />
      </div>
      <Motivo id={idMotivo} cuando={!puedeResolver}>{MOTIVO_SIN_PERMISO}</Motivo>
      {descartando && puedeResolver && (
        <div className="space-y-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
          <label className="block text-sm">
            Por qué se descarta (queda en la bitácora)
            <input className="entrada mt-1 w-full" value={nota} onChange={(e) => setNota(e.target.value)} />
          </label>
          <Motivo id={`motivo-nota-${ev.id}`} cuando={!notaLista}>Escribe el motivo (al menos tres letras) para poder descartar.</Motivo>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!notaLista || enviando}
              aria-describedby={!notaLista ? `motivo-nota-${ev.id}` : undefined}
              onClick={() => resolver('DESCARTADO')}
              className="inline-flex min-h-11 items-center rounded-lg bg-red-600 px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {enviando ? 'Un momento…' : 'Sí, descartar'}
            </button>
            <button type="button" onClick={() => setDescartando(false)} className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm">No</button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function PorRevisar({ t, puedeResolver = false, onCerrar }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [visibles, setVisibles] = useState(POR_PAGINA);

  const cargar = useCallback(() => {
    api.get(`/terminales/${t.id}/eventos?estado=POR_REVISAR`)
      .then(setDatos)
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo leer la lista. Vuelve a intentarlo.'));
  }, [t.id]);
  useEffect(cargar, [cargar]);

  const eventos = datos?.eventos || [];
  return (
    <div className="space-y-4 max-w-3xl">
      <header className="space-y-1">
        <button type="button" onClick={onCerrar} className="min-h-11 text-sm font-semibold text-gray-700">← Volver a Terminales</button>
        <h1 className="text-xl font-bold text-gray-900">Por revisar en {t.nombre}</h1>
        <p className="text-sm text-gray-600">
          Movimientos que esta terminal capturó sin red o que no cuadraron con lo que había. Aquí se aprueban o se descartan.
          <Ayuda id="terminal.revisar.pantalla" />
        </p>
      </header>
      {aviso && <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">{aviso}</div>}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}
      {!datos && !error && <Cargando />}
      {datos && eventos.length === 0 && (
        <div className="border border-gray-200 bg-white rounded-xl p-5 text-center space-y-2">
          <div className="font-semibold text-gray-900">Nada por revisar</div>
          <p className="text-sm text-gray-600">
            Aquí aparecen los movimientos que la terminal capturó sin red o que no cuadraron con lo que había; cada uno se aprueba o se descarta.
          </p>
        </div>
      )}
      {eventos.length > 0 && (
        <ul className="space-y-2">
          {eventos.slice(0, visibles).map((ev) => (
            <Fila
              key={ev.id}
              ev={ev}
              puedeResolver={puedeResolver}
              onResuelto={(_, mensaje) => { setError(null); setAviso(mensaje); cargar(); }}
              onError={setError}
            />
          ))}
        </ul>
      )}
      {eventos.length > visibles && (
        <button type="button" onClick={() => setVisibles((v) => v + POR_PAGINA)} className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold">
          Ver más ({eventos.length - visibles} más)
        </button>
      )}
    </div>
  );
}
